<script lang="ts">
  import { onMount } from 'svelte';
  import { Button, ButtonSet, filterTreeByText, Search, TreeView, InlineNotification, SkeletonText } from 'carbon-components-svelte';

  import Events16 from 'carbon-icons-svelte/lib/Events16';
  import Finance16 from 'carbon-icons-svelte/lib/Finance16';
  import Launch16 from 'carbon-icons-svelte/lib/Launch16';

  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';
  import type { TreeNode } from 'carbon-components-svelte/src/TreeView/TreeView.svelte';

  import { getSLATaskAffectedClients } from '../../../services';
  import { DataInventoryServiceIdToName } from '../../../../shared/constants';

  import type {
    SlaAffectedClientsData,
    SlaAffectedClient,
    SlaAffectedFeedService,
    SlaAffectedSubscription,
  } from '../../../../shared/types/sla-task-types';

  type ExtentedTreeNode = TreeNode & {
    nav?: { href: string };
    nodes?: ExtentedTreeNode[];
  };

  const serviceIdToName = (id: number): string => DataInventoryServiceIdToName[id as keyof typeof DataInventoryServiceIdToName];

  export let slaTaskId: string;

  const VIEW_BY_CLIENT = 'by-client';
  const VIEW_BY_FEED = 'by-feed';

  let data: SlaAffectedClientsData | null = null,
    isLoading = true,
    resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null,
    searchQuery = '',
    viewMode: typeof VIEW_BY_CLIENT | typeof VIEW_BY_FEED = VIEW_BY_CLIENT,
    treeView: { expandAll: () => void; collapseAll: () => void } | undefined;

  const pairKey = (...parts: (string | number)[]): string => parts.join('::');

  const groupHref = (groupId: string): string => `/group/${groupId}`;
  const dataFeedHref = (feedId: string): string => `/data-feed/${feedId}`;
  const permissionHref = ({
    groupId,
    permissionId,
    permissionType,
  }: Pick<SlaAffectedSubscription, 'groupId' | 'permissionId' | 'permissionType'>): string =>
    `/group/${groupId}?permissionId=${permissionId}&page=1&pageSize=5#permissions-${permissionType}`;

  function node(id: string, text: string, href: string | null, nodes: ExtentedTreeNode[] = []): ExtentedTreeNode {
    return { id, text, ...(href ? { nav: { href } } : {}), ...(nodes.length ? { nodes } : {}) };
  }

  function buildClientView(clients: SlaAffectedClient[], subscriptions: SlaAffectedSubscription[]): ExtentedTreeNode[] {
    return clients.map((client) => {
      const feedNodes = subscriptions.reduce<ExtentedTreeNode[]>((all, subscription) => {
        if (subscription.groupId !== client.groupId) return all;
        const feedService = feedServiceByPair.get(pairKey(subscription.feedId, subscription.serviceId));
        if (!feedService) return all;
        return [
          ...all,
          node(
            pairKey(client.groupId, subscription.permissionId, subscription.feedId),
            `${feedService.feedName} — ${serviceIdToName(subscription.serviceId)}`,
            permissionHref(subscription)
          ),
        ];
      }, []);

      const userNodes = client.users.map((user, index) =>
        node(pairKey(client.groupId, 'user', index), `${user.name} (${user.email})`, null)
      );

      return node(client.groupId, `${client.groupName} (${client.users.length} users)`, groupHref(client.groupId), [
        node(pairKey(client.groupId, 'feeds'), 'Feeds', null, feedNodes),
        node(pairKey(client.groupId, 'users'), `Users (${client.users.length})`, null, userNodes),
      ]);
    });
  }

  function buildFeedView(feedServices: SlaAffectedFeedService[], subscriptions: SlaAffectedSubscription[]): ExtentedTreeNode[] {
    return feedServices.map((feedService) => {
      const feedPair = pairKey(feedService.feedId, feedService.serviceId);

      const clientNodes = subscriptions.reduce<ExtentedTreeNode[]>((all, subscription) => {
        if (subscription.feedId !== feedService.feedId || subscription.serviceId !== feedService.serviceId) return all;
        const client = clientsById.get(subscription.groupId);
        if (!client) return all;

        const userNodes = client.users.map((user, index) =>
          node(pairKey(feedPair, subscription.groupId, 'user', index), `${user.name} (${user.email})`, null)
        );

        return [
          ...all,
          node(
            pairKey(feedPair, subscription.groupId),
            `${client.groupName} (${client.users.length} users)`,
            permissionHref(subscription),
            userNodes
          ),
        ];
      }, []);

      return node(
        feedPair,
        `${feedService.feedName} — ${serviceIdToName(feedService.serviceId)}`,
        dataFeedHref(feedService.feedId),
        clientNodes
      );
    });
  }

  async function loadAffectedClients(): Promise<void> {
    try {
      data = await getSLATaskAffectedClients(slaTaskId);
    } catch (err) {
      console.error('[AffectedSlaClients] Failed to load affected clients', err);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load affected clients. Please try again later.',
      };
    } finally {
      isLoading = false;
    }
  }

  function openEntity(event: MouseEvent, href: string): void {
    event.stopPropagation();
    window.open(href, '_blank', 'noopener');
  }

  function nodeHref(node: unknown): string | undefined {
    return (node as ExtentedTreeNode).nav?.href;
  }

  function handleNodeClick(event: MouseEvent, node: unknown): void {
    const href = nodeHref(node);
    if (href) openEntity(event, href);
  }

  $: hasSubscriptions = (data?.clients?.length ?? 0) > 0 || (data?.subscriptions?.length ?? 0) > 0;

  $: clientsById = new Map<string, SlaAffectedClient>((data?.clients ?? []).map((client) => [client.groupId, client]));
  $: feedServiceByPair = new Map<string, SlaAffectedFeedService>(
    (data?.feedServices ?? []).map((fs) => [pairKey(fs.feedId, fs.serviceId), fs])
  );
  $: subscriptions = data?.subscriptions ?? [];

  $: clientView = buildClientView(data?.clients ?? [], subscriptions);
  $: feedView = buildFeedView(data?.feedServices ?? [], subscriptions);

  $: query = searchQuery.trim().toLowerCase();

  $: baseNodes = viewMode === VIEW_BY_CLIENT ? clientView : feedView;
  $: treeNodes = query ? (filterTreeByText(baseNodes, query) as ExtentedTreeNode[]) : baseNodes;

  onMount(loadAffectedClients);
</script>

{#if resultNotification}
  <InlineNotification hideCloseButton lowContrast {...resultNotification} />
{:else if isLoading}
  <SkeletonText paragraph lines={4} />
{:else}
  {#if hasSubscriptions}
    <div class="controls">
      <ButtonSet>
        <Button
          iconDescription="By client"
          tooltipPosition="top"
          icon={Events16}
          kind={viewMode === VIEW_BY_CLIENT ? 'primary' : 'tertiary'}
          on:click={() => (viewMode = VIEW_BY_CLIENT)}
        />
        <Button
          iconDescription="By feed"
          tooltipPosition="top"
          icon={Finance16}
          kind={viewMode === VIEW_BY_FEED ? 'primary' : 'tertiary'}
          on:click={() => (viewMode = VIEW_BY_FEED)}
        />
      </ButtonSet>
      <Search placeholder="Search clients, feeds, or users..." bind:value={searchQuery} />
    </div>
  {/if}

  {#if treeNodes.length === 0}
    <p class="empty-state">No affected clients found.</p>
  {:else}
    <div class="tree-controls">
      <Button kind="ghost" size="small" on:click={() => treeView?.expandAll()}>Expand all</Button>
      <Button kind="ghost" size="small" on:click={() => treeView?.collapseAll()}>Collapse all</Button>
    </div>
    <TreeView bind:this={treeView} nodes={treeNodes} let:node>
      <span class="node">
        <span class="node-text">{node.text}</span>
        {#if nodeHref(node)}
          <Button hideTooltip kind="ghost" size="small" icon={Launch16} on:click={(event) => handleNodeClick(event, node)} />
        {/if}
      </span>
    </TreeView>
  {/if}
{/if}

<style>
  .controls {
    display: flex;
  }

  .controls :global(.bx--btn-set .bx--btn) {
    width: unset;
  }

  .tree-controls {
    background-color: var(--cds-ui-01);
  }

  .empty-state {
    color: var(--cds-text-02);
    padding: 1rem 0;
  }

  .node {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
</style>
